import { Menu, Transition } from '@headlessui/react';
import { ChevronDownIcon } from '@heroicons/react/24/outline';
import { Fragment, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { apiUrl } from '../../api/apiUrl';
import http from '../../api/axios';
import { useAuth } from '../../context/AuthContext';
import { useTranslation } from '../../context/translationContext';
import { useCurrentCompany } from '../../hooks/useCompany';
import { Company } from '../../types/Company';
import { companyNavigation, userNavigation } from '../../utils/appNavigation';

export default function SideBar() {
  const [companies, setCompanies] = useState<Company[] | null>(null);
  const { company, changeCompany } = useCurrentCompany();
  const { isAuthenticated } = useAuth();
  const { t } = useTranslation();

  const fetchCompanies = async () => {
    const response = await http.apiGet({ url: apiUrl.COMPANY_GET_USERS_COMPANIES });
    if (response?.data) {
      setCompanies(response?.data);
    }
  };

  useEffect(() => {
    if (!companies || company) return;
    if (companies.length > 0) changeCompany(companies[0]);
  }, [companies]);

  useEffect(() => {
    if (!companies || !isAuthenticated) return;
    fetchCompanies();
  }, [company]);

  useEffect(() => {
    if (!isAuthenticated) return;
    fetchCompanies();
  }, [isAuthenticated]);

  if (companies === null) return <>Loading...</>;
  return (
    <aside
      className="w-64 drop-shadow-lg shadow-lg left-0 sticky top-0 h-screen py-2 px-3 bg-50 rounded-tr"
      aria-label="Sidebar"
    >
      <div className="w-56 text-right">
        <Menu as="div" className="text-left">
          <div>
            <Menu.Button className="inline-flex w-full justify-between rounded-md bg-gray-600 px-4 py-2 text-lg font-medium text-white hover:bg-opacity-30 focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-opacity-75">
              {company ? company.name : 'Select Company'}
              <ChevronDownIcon
                className="ml-2 -mr-1 h-5 mt-1 w-5 text-violet-200 hover:text-violet-100"
                aria-hidden="true"
              />
            </Menu.Button>
          </div>
          <Transition
            as={Fragment}
            enter="transition ease-out duration-100"
            enterFrom="transform opacity-0 scale-95"
            enterTo="transform opacity-100 scale-100"
            leave="transition ease-in duration-75"
            leaveFrom="transform opacity-100 scale-100"
            leaveTo="transform opacity-0 scale-95"
          >
            <Menu.Items className="absolute right-0 mt-2 w-56 origin-top-right divide-y divide-gray-100 rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
              <div className="px-1 py-1">
                {companies?.map((company, idx) => (
                  <Menu.Item key={idx}>
                    {({ active }) => (
                      <button
                        className={`${
                          active ? 'bg-violet-500 text-white' : 'text-gray-900'
                        } group flex w-full items-center rounded-md px-2 py-2 text-sm`}
                        onClick={() => changeCompany(company)}
                      >
                        {company?.name}
                      </button>
                    )}
                  </Menu.Item>
                ))}
              </div>
            </Menu.Items>
          </Transition>
        </Menu>
      </div>
      <ul className="pt-4 space-y-2">
        {companyNavigation.map((e, idx) => {
          const Icon = e.icon;
          return (
            <li key={idx}>
              <Link
                className="flex p-2 bg-transparent hover:bg-gray-600 hover:rounded text-gray-600 hover:text-white"
                to={e.path}
              >
                <Icon className="h-6 w-6 ml-5" />
                <span className="ml-6">{t(e.name)}</span>
              </Link>
            </li>
          );
        })}
      </ul>
      <ul className="pt-4 mt-4 space-y-2 border-t border-gray-200 dark:border-gray-700">
        {userNavigation.map((e, idx) => {
          const Icon = e.icon;
          return (
            <li key={idx}>
              <Link
                className="flex p-2 bg-transparent hover:bg-gray-600 hover:rounded text-gray-600 hover:text-white"
                to={e.path}
              >
                <Icon className="h-6 w-6 ml-5" />
                <span className="ml-6">{t(e.name)}</span>
              </Link>
            </li>
          );
        })}
      </ul>
    </aside>
  );
}
